import {
  Alert,
  Anchor,
  Badge,
  Box,
  Center,
  Code,
  Container,
  Grid,
  Group,
  Loader,
  ScrollArea,
  Switch,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getImport, getMappingDetails } from "../api";
import { Link } from "wouter";

// Helper components (identical from original files)
function MappingErrorTooltip({ failures }) {
  const { t } = useTranslation("imports");
  return failures.map((failure, index) => (
    <Box key={index} mb="sm">
      <Text fz="sm" fw={600}>
        {failure.type}{" "}
        {(failure.source || failure.dest) && (
          <Code>
            {failure.source} {"\u27F6"} {failure.dest}
          </Code>
        )}
      </Text>
      <Text fz="sm" c="dimmed">
        {failure.message}
      </Text>
      {failure.attemptedValue && (
        <>
          <Text fz="sm">
            {t("mappings.details.attemptedValue")}{" "}
            <Code>{failure.attemptedValue}</Code>
          </Text>
        </>
      )}
    </Box>
  ));
}

function MappingFailure({ failures }) {
  if (!failures || failures.length === 0) return " - ";
  let color = "yellow";
  if (failures.some((f) => f.severity === "error")) color = "red";
  return (
    <Tooltip
      maw="40%"
      multiline
      label={<MappingErrorTooltip failures={failures} />}
      position="top"
      withArrow
    >
      <Badge color={color}>{failures.length}</Badge>
    </Tooltip>
  );
}

function MappingDetails({ params }) {
  const { t } = useTranslation("imports");
  const [importData, setImportData] = useState(null);
  const [showAllAttributes, setShowAllAttributes] = useState(false); // Used for toggling attribute display
  const [mapping, setMapping] = useState(null);

  useEffect(() => {
    getMappingDetails(params.mappingId).then((data) => {
      setMapping(data);
    });
  }, [params.mappingId]);

  useEffect(() => {
    getImport(params.importId).then((data) => {
      setImportData(data);
    });
  }, [params.importId]);

  if (!importData) {
    return (
      <Container
        fluid
        style={{ width: "calc(100dvw - 2 * var(--mantine-spacing-md))" }}
      >
        <Center>
          <Loader />
        </Center>
      </Container>
    );
  }

  const displayColumns = showAllAttributes
    ? importData.columns
    : importData.columns.slice(0, 4);

  const keys = importData.columns?.map((p) => p.key);
  if (!keys) {
    return <div>{t("meta.noColumns")}</div>;
  }

  const itemsToDisplay = importData.productVariantProtos;

  return (
    <Container
      fluid
      style={{ width: "calc(100dvw - 2 * var(--mantine-spacing-md))" }}
    >
      <Anchor onClick={() => window.history.back()} c="gray">
        ← {t("common:generic.goBack")}
      </Anchor>
      {/* <Code block>{JSON.stringify(mapping, null, 2)}</Code>
       */}

      {mapping?.process?.abortReason && (
        <Alert
          color="red"
          title={t("mappings.details.abortedTitle")}
          mb="md"
        >
          {t("mappings.aborted", { reason: mapping.process.abortReason })}
        </Alert>
      )}

      <Grid>
        <Grid.Col span={12}>
          <Group mb="sm" justify="space-between">
            <Link to={`/imports/${importData.id}`}>
              <Text fz="sm" fw={600} mb="sm">
                {importData.originalName}
              </Text>
            </Link>

            <Text fz="sm" c="dimmed">
              {t("mappings.details.skuCount", {
                count: importData.productVariantProtos.length,
              })}
            </Text>

            <Switch
              checked={showAllAttributes}
              label={t("mappings.details.showAllAttributes")}
              onChange={() => setShowAllAttributes((prev) => !prev)}
            />
          </Group>
          <ScrollArea type="always" h="80dvh" w="100%">
            <Table stickyHeader fz="xs" withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={30}>{t("rowErrors.row")}</Table.Th>
                  <Table.Th w={30}>{t("mappings.details.issues")}</Table.Th>
                  <Table.Th w={40} style={{ borderRight: "1px solid var(--mantine-color-default-border)" }}>
                    {t("mappings.details.attributes")}
                  </Table.Th>
                  {displayColumns.map((column) => (
                    <Table.Th key={column.key} style={{ whiteSpace: "nowrap" }}>
                      {column.key}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                {itemsToDisplay.map((productVariantProto, index) => (
                  <Table.Tr key={productVariantProto.id}>
                    <Table.Td ta="right">{index + 1}</Table.Td>
                    <Table.Td ta="right">
                      <MappingFailure
                        failures={
                          mapping?.issues?.find(
                            (p) => p.protoId === productVariantProto.id
                          )?.failures
                        }
                      />
                    </Table.Td>
                    <Table.Th
                      ta="right"
                      style={{ borderRight: "1px solid var(--mantine-color-default-border)" }}
                    >
                      {keys.length}
                    </Table.Th>
                    {displayColumns.map(({ key }) => (
                      <Table.Td
                        style={{ whiteSpace: "nowrap" }}
                        key={`${productVariantProto.id}-${key}`}
                      >
                        {productVariantProto.fieldValues[key]}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Grid.Col>
      </Grid>
    </Container>
  );
}

export default MappingDetails;
